import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "motion/react";
import { BrandLogo } from "@/components";
import {
  Activity,
  Bell,
  Building2,
  Camera,
  CheckCircle2,
  HeartHandshake,
  KeyRound,
  ListChecks,
  Mail,
  Menu as MenuIcon,
  MessageCircle,
  MoreHorizontal,
  Search,
  Settings,
  Sparkles,
  Trash2,
  TreePine,
  UserCog,
  type LucideIcon,
} from "lucide-react";
import { AppBar, BottomNavBar, SideRail, TopBarAction, PageTitle, DesktopTopBar, CommandPalette, NotificationsPopover, ProfileMenu, profileEntriesFor, SkipLink, type NavItem, type CommandPaletteItem, type ProfileMenuEntry } from "@/components";
import { cn } from "@/lib/utils";
import { M3BottomSheet as BottomSheet } from "@/components/M3BottomSheet";
import { Counter, PageTransition, Stagger, StaggerItem } from "@/components/FadeIn";
import { Button } from "@/components/ui/button";
import { useRequireUser } from "@/lib/hooks/useAuthGate";
import { useShortcut, useChord, modKeyDisplay } from "@/lib/hooks/useGlobalShortcuts";
import { ModuleSlotProvider, useModuleSlot } from "@/lib/ModuleContext";
import { useSupabaseQuery } from "@/lib/dbHooks";
import { isAuthConfigured, supabase } from "@/lib/supabase";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import "@/index.css";
import { getFab, subscribeFab } from "@/components/FabRegistry";
import { ResponsiveFab } from "@/components/ResponsiveFab";
import { useNotifications } from "@/pages/panels/notifications";
import { SearchPanel } from "@/pages/panels/SearchPanel";
import { OrgPickerSheet } from "@/pages/panels/OrgPickerSheet";
import { NotificationsPanel } from "@/pages/panels/NotificationsPanel";
import { FamStatusView } from "@/pages/modules/FamStatusView";
import { FamListView } from "@/pages/modules/FamListView";
import { FamTreeView } from "@/pages/modules/FamTreeView";
import { FamilyChatView } from "@/pages/modules/FamilyChatView";
import { FamMatchView } from "@/pages/modules/FamMatchView";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useAvatarUpload, type AvatarUploadPhase } from "@/lib/useAvatarUpload";
import { TodayView } from "@/pages/modules/TodayView";
import { ModuleMembersView } from "@/pages/modules/ModuleMembersView";

export type ModuleId =
  | "today"
  | "famchat"
  | "fammatch"
  | "famtree"
  | "famstatus"
  | "famlist"
  | "fammembers";

// The 4 modules in the bottom navigation bar (M3 / Flutter pattern).
// Simplified scope: today + FamStatus + FamChat + FamList.
const primaryNav: NavItem<ModuleId>[] = [
  { id: "today", label: "今日", icon: Sparkles },
  { id: "famstatus", label: "打卡", icon: Activity },
  { id: "famchat", label: "家人", icon: MessageCircle, badge: "3" },
  { id: "famlist", label: "清單", icon: ListChecks },
];

// The remaining modules live behind the "More" sheet.
// Tree + Match live in the overflow so the bottom bar stays
// at four high-frequency destinations.
const moreNav: NavItem<ModuleId>[] = [
  { id: "fammatch", label: "FamMatch", icon: HeartHandshake },
  { id: "famtree", label: "FamTree", icon: TreePine },
];

const allNav: NavItem<ModuleId>[] = [...primaryNav, ...moreNav];

type Panel = "more" | "menu" | "search" | "notif" | "settings" | "orgpicker" | "avatar" | "password" | null;
/**
 * Round avatar used by the top bar, menu sheet, settings sheet, etc.
 * Falls back to initials over the rose gradient when no URL is set,
 * preserving the pre-image-app behaviour. When `avatarUrl` is
 * provided we render the image with object-fit: cover and ARIA-hide
 * the redundant text initials so screen readers see a single
 * labelled element rather than overlapping announcements.
 */
function ProfileAvatar({
  initials,
  avatarUrl,
  size = "lg",
}: {
  initials: string;
  avatarUrl?: string | null;
  size?: "sm" | "md" | "lg";
}) {
  const dim = size === "sm" ? "h-9 w-9 text-sm" : size === "md" ? "h-10 w-10 text-sm" : "h-12 w-12 text-base";
  if (avatarUrl) {
    return (
      <span
        aria-hidden
        className={cn(
          "grid shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-rose-400 to-rose-600 font-semibold text-white",
          dim,
        )}
      >
        <img
          src={avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          // Cache-bust: the bucket is public and Storage sets long cache
          // headers, so when avatar_url flips to a new object the previous
          // image can linger in the browser cache. Adding a key tied to the
          // URL itself forces a fresh fetch only when the URL changes.
          key={avatarUrl}
          onError={(e) => {
            // Storage deleted the underlying object (e.g. orphaned file
            // was pruned). Hide the broken image and let the initials
            // fall through visually next render.
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <span className="sr-only">{initials}</span>
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-rose-400 to-rose-600 font-semibold text-white",
        dim,
      )}
    >
      {initials}
    </span>
  );
}

export function AppShell() {
  const [active, setActive] = useState<ModuleId>("today");
  const [panel, setPanel] = useState<Panel>(null);
  const fab = useSyncExternalStore(subscribeFab, getFab, getFab);

  useEffect(() => {
    if (panel === null) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [panel]);

  // Lock the whole document while the chat fills the viewport. The chat
  // owns its own internal scroller, so any document-level scroll would
  // double-scroll the conversation.
  useEffect(() => {
    if (active !== "famchat") return;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [active]);

  const me = useCurrentUser();
  const notif = useNotifications(me.user?.id ?? null);
  // Avatar upload state lives at shell scope so the Settings rows,
  // the avatar sheet, and the avatar preview at the top of the
  // Settings sheet all see the same in-flight indicator.
  const avatarUpload = useAvatarUpload();

  const goTo = (id: ModuleId) => {
    setActive(id);
    setPanel(null);
  };
  const [paletteOpen, setPaletteOpen] = useState(false);
  const moduleSlot = useModuleSlot();
  useShortcut({ key: "k", mod: true, handler: () => setPaletteOpen((v) => !v) });
  useShortcut({
    key: "Escape",
    handler: () => {
      if (paletteOpen) setPaletteOpen(false);
      else if (panel) setPanel(null);
    },
  });
  useChord("g", {
    t: () => goTo("today"),
    s: () => goTo("famstatus"),
    c: () => goTo("famchat"),
    l: () => goTo("famlist"),
    r: () => goTo("famtree"),
    h: () => goTo("fammatch"),
    u: () => goTo("fammembers"),
  });
  // Panel selectors — derived from `panel` for declarative JSX.
  const moreOpen    = panel === "more";
  const menuOpen    = panel === "menu";
  const searchOpen  = panel === "search";
  const notifOpen   = panel === "notif";
  const settingsOpen = panel === "settings";
  const orgPickerOpen = panel === "orgpicker";
  const avatarOpen  = panel === "avatar";
  const passwordOpen = panel === "password";
  const openPanel = (p: Exclude<Panel, null>) =>
    setPanel((cur) => (cur === p ? null : p));
  const closePanel = () => {
    // Reset transient state owned by the various sheets so a stale
    // "done / error" notice from a previous photo upload doesn't
    // follow the user back into the Settings sheet.
    avatarUpload.reset();
    setPanel(null);
  };

  useRequireUser({ redirectTo: "/login" });

  // Role gate: app admins (system admins with profiles.is_admin = true)
  // don't belong on the regular user app. Wait for the admin lookup
  // to settle, then send them to /admin if they're an admin. The
  // `admin` discriminated union ("pending" | "admin" | "not_admin")
  // is atomic — there's no race between "checked" and "isAdmin" like
  // with two separate booleans, so the previous redirect-loop bug
  // (where /admin briefly thought an admin was a non-admin and
  // redirected back to /app, which then bounced them to /admin
  // again) is impossible.
  const me2 = useCurrentUser();
  useEffect(() => {
    if (me2.loading) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/auth/callback") return;
    if (me2.admin === "admin") {
      window.location.replace("/admin");
    }
  }, [me2.loading, me2.admin]);

  // Family-admin state. profiles.role is the single source of truth
  // (migrations 021/024): a user is either a family_admin (LITE
  // family-app user with admin rights in their own family) or a
  // family_member. The family_members table is read for membership
  const isFamilyAdmin = me.role === "family_admin";

  // Resolve the current family + its org links. Post-04901: the
  // family's org list lives in `family_org_links`, exposed by the
  // hook as `cf.orgIds`. Single-org consumers read `orgIds[0]`.
  const cf = useCurrentFamily(me.user?.id ?? null);
  const currentOrgId = cf.orgIds[0] ?? null;
  const currentOrg = useSupabaseQuery<{ name: string; id: string } | null>(async () => {
    if (!isAuthConfigured() || !currentOrgId) return { data: null, error: null };
    const { data, error } = await supabase
      .from("orgs")
      .select("id, name")
      .eq("id", currentOrgId)
      .maybeSingle();
    return { data: data ?? null, error };
  }, [currentOrgId, isFamilyAdmin]);

  // Command palette items. Modules go in a "切換模組" section,
  // family members in a "家人" section, and recent notifications
  // in a "動態" section. Re-built only when the underlying data
  // shifts; not on every render.
  const paletteItems: CommandPaletteItem[] = useMemo(() => {
    const moduleSection: CommandPaletteItem[] = allNav.map((it) => ({
      id: `module:${it.id}`,
      section: "切換模組",
      label: it.label,
      hint: "切換到這個模組",
      icon: <it.icon className="h-4 w-4" aria-hidden />,
      keywords: [it.id, it.label, "module"],
      onSelect: () => goTo(it.id),
    }));
    const memberSection: CommandPaletteItem[] = cf.members.map((m) => ({
      id: `member:${m.id}`,
      section: "家人",
      label: m.display_name,
      hint: m.relation_label || m.relation || "家人",
      icon: (
        <span
          aria-hidden
          className={cn(
            "grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br text-[10px] font-semibold text-white",
            m.avatar_color,
          )}
        >
          {m.initials}
        </span>
      ),
      keywords: [m.display_name, m.relation_label, m.relation, "member"],
      onSelect: () => goTo("famtree"),
    }));
    const notifSection: CommandPaletteItem[] = notif.items.slice(0, 8).map((n) => ({
      id: `notif:${n.id}`,
      section: "動態",
      label: n.title,
      hint: `${n.body} · ${n.time}`,
      icon: <Bell className="h-4 w-4" aria-hidden />,
      keywords: [n.kind, n.title, n.body, "notification"],
      onSelect: () => {
        notif.markRead(n.id);
        goTo(n.target);
      },
    }));
    return [...moduleSection, ...memberSection, ...notifSection];
  }, [cf.members, notif.items, notif.markRead, goTo]);

  // Profile menu entries. Shared by the desktop popover trigger
  // (in the top bar) and the mobile menu sheet. The family-admin
  // "管理家庭成員" action is NOT part of this menu anymore — on the
  // wide viewport it is a dedicated icon button in the top bar
  // (see the trailing cluster below).
  const profileEntries: ProfileMenuEntry[] = useMemo(
    () => profileEntriesFor({
      isOrgAdmin: me.role === "org_admin",
      onOpenSearch: () => setPaletteOpen(true),
      onOpenSettings: () => openPanel("settings"),
      onOpenTree: () => goTo("famtree"),
      onGotoOrg: () => { window.location.href = "/org"; },
    }),
    [me.role, openPanel, setPaletteOpen, goTo],
  );
  return (
    <ModuleSlotProvider>
    <SkipLink />
    <div className="flex min-h-screen flex-col bg-background text-foreground lg:h-screen lg:flex-row lg:overflow-hidden">
      <AppBar
        leading={
          <button
            type="button"
            aria-label="開啟目錄"
            onClick={() => openPanel("menu")}
            className="-ml-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/85 transition-colors hover:bg-secondary active:bg-secondary/70"
          >
            <MenuIcon className="h-5 w-5" aria-hidden />
          </button>
        }
        brand={
          <div className="flex min-w-0 items-center gap-2">
            <a href="/" className="flex shrink-0 items-center" aria-label="FamChest 傳琛">
              <BrandLogo className="h-7 w-auto sm:h-8" />
            </a>
            <span className="truncate text-sm font-medium text-foreground/85">
              {allNav.find((n) => n.id === active)?.label ?? "今日"}
            </span>
          </div>
        }
        trailing={
          <span className="flex items-center gap-0.5">
            <button
              type="button"
              aria-label="搜尋"
              onClick={() => openPanel("search")}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/85 transition-colors hover:bg-secondary active:bg-secondary/70"
            >
              <Search className="h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="通知"
              onClick={() => openPanel("notif")}
              className="relative inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/85 transition-colors hover:bg-secondary active:bg-secondary/70"
            >
              <Bell className="h-5 w-5" aria-hidden />
              {notif.unreadCount > 0 && (
                <span
                  aria-label={`${notif.unreadCount} 則未讀`}
                  className="absolute right-1.5 top-1.5 grid min-h-[16px] min-w-[16px] place-items-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground ring-2 ring-background"
                >
                  {notif.unreadCount}
                </span>
              )}
            </button>
          </span>
        }
      />
      {/* ─────────────────────  Desktop rail (lg+)  ───────────────────── */}
      <SideRail<ModuleId>
        items={allNav}
        activeId={active}
        onSelect={setActive}
        footer={
          <button
            type="button"
            aria-label="設定"
            onClick={() => openPanel("menu")}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-foreground/70 transition-colors hover:bg-secondary"
          >
            <Settings className="h-5 w-5" aria-hidden />
          </button>
        }
      />

      {/* ─────────────────────  Main column (top bar + content + context rail)  ───────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
      <DesktopTopBar
        breadcrumb={
          <PageTitle
            icon={(() => {
              const Icon = allNav.find((n) => n.id === active)?.icon;
              return Icon ? <Icon className="h-4 w-4" aria-hidden /> : null;
            })()}
            title={moduleSlot?.pageTitle ?? allNav.find((n) => n.id === active)?.label ?? "今日"}
            subtitle={moduleSlot?.pageSubtitle ?? currentOrg.data?.name ?? cf.family?.name ?? undefined}
          />
        }
        trailing={
          <>
            <button
              type="button"
              aria-label="搜尋（指令面板）"
              onClick={() => setPaletteOpen(true)}
              className="hidden h-10 items-center gap-1.5 rounded-xl border border-border/60 bg-secondary/40 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary sm:inline-flex"
            >
              <Search className="h-3.5 w-3.5" aria-hidden />
              <span>搜尋</span>
              <kbd className="ml-1 rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono text-[10px]">
                {modKeyDisplay()}K
              </kbd>
            </button>
            <NotificationsPopover
              items={notif.items}
              unreadCount={notif.unreadCount}
              onMarkAllRead={notif.markAllRead}
              onMarkRead={notif.markRead}
              onNavigate={goTo}
              trigger={
                <TopBarAction
                  icon={Bell}
                  label="通知"
                  badge={notif.unreadCount > 0 ? notif.unreadCount : undefined}
                />
              }
            />
            {/* 管理家庭成員 — family-admin action on the wide viewport.
             * Moved out of the profile menu into this dedicated icon
             * button (mobile keeps its entry in the settings sheet). */}
            {isFamilyAdmin && (
              <TopBarAction
                icon={UserCog}
                label="管理家庭成員"
                onClick={() => goTo("fammembers")}
              />
            )}
            <ProfileMenu
              displayName={me.displayName}
              email={me.email}
              initials={me.avatarInitials}
              avatarUrl={me.avatarUrl}
              avatarColor="from-rose-400 to-rose-600"
              entries={profileEntries}
              onSignOut={() => me.signOut().then(() => { window.location.href = "/login"; })}
              trigger={
                <span
                  aria-label="開啟個人選單"
                  className="grid h-9 w-9 cursor-pointer place-items-center overflow-hidden rounded-full bg-gradient-to-br from-rose-400 to-rose-600 text-sm font-semibold text-white"
                >
                  {me.avatarUrl ? (
                    <img
                      src={me.avatarUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      key={me.avatarUrl}
                    />
                  ) : (
                    me.avatarInitials
                  )}
                </span>
              }
            />
          </>
        }
      />
      <main
        id="app-main"
        className={cn(
          // `flex flex-col` lets the inner content wrapper use `flex-1`
          // for routes (like FamChat) that want to fill the scroll
          // viewport instead of scrolling. Other routes leave the
          // content sized by its natural height.
          "flex-1 min-h-0 flex-col overflow-y-auto",
          // Only the chat route publishes no extra content below the
          // module, so flipping the wrapper to `flex-1` is safe there.
          // For every other route `flex-1` would stretch an empty
          // footer area below the actual content, which we don't want.
          active === "famchat" ? "flex" : "block",
        )}
      >
        <div
          className={cn(
            "mx-auto flex w-full max-w-3xl flex-col px-3 pb-28 pt-3 sm:px-4",
            "lg:max-w-5xl lg:px-8 lg:pb-10",
            "xl:max-w-6xl",
            // When the module publishes a context rail, lay it out
            // as a 2-pane (content + rail) grid.
            moduleSlot?.context && "xl:grid xl:grid-cols-[minmax(0,1fr)_320px] xl:gap-6 xl:items-start",
            // Fill <main> end-to-end on the chat route so the chat
            // card's own `flex-1` actually has a bounded parent to
            // expand into (and the composer can sit flush above the
            // bottom nav without pixel math).
            active === "famchat" && "min-h-0 flex-1",
          )}
        >
          <div className={cn("min-w-0", active === "famchat" && "flex min-h-0 flex-1 flex-col")}>
            <AnimatePresence mode="wait" initial={false}>
              <PageTransition
                key={active}
                id={active}
                className={active === "famchat" ? "flex min-h-0 flex-1 flex-col" : undefined}
              >
                {active === "today" ? (
                  <TodayView navigate={goTo} />
                ) : active === "famstatus" ? (
                  <FamStatusView />
                ) : active === "famlist" ? (
                  <FamListView />
                ) : active === "famtree" ? (
                  <FamTreeView />
                ) : active === "fammatch" ? (
                  <FamMatchView />
                ) : active === "famchat" ? (
                  <FamilyChatView />
                ) : active === "fammembers" ? (
                  <ModuleMembersView />
                ) : (
                  <ModulePlaceholder id={active} />
                )}
              </PageTransition>
            </AnimatePresence>
          </div>
          {moduleSlot?.context && (
            <aside
              aria-label={moduleSlot?.pageTitle ? `${moduleSlot.pageTitle} 側欄` : "內容側欄"}
              data-context-rail
              className="hidden xl:flex xl:flex-col xl:gap-4 xl:sticky xl:top-20"
            >
              {moduleSlot.context}
            </aside>
          )}
        </div>
      </main>
      </div>
      {/* Shared module action. Compact above the mobile bottom bar and
          extended at wide viewports beside the desktop side rail. */}
      <ResponsiveFab action={fab} />

      {/* ─────────────────────  Bottom navigation (M3)  ───────────────────── */}
      <BottomNavBar<ModuleId>
        items={primaryNav}
        activeId={active}
        onSelect={goTo}
        overflow={{
          icon: MoreHorizontal,
          label: "更多模組",
          onClick: () => openPanel("more"),
          active: moreNav.some((n) => n.id === active),
        }}
      />

      {/* ─────────────────────  More bottom sheet  ───────────────────── */}
      <AnimatePresence>
      {moreOpen && (
        <BottomSheet onClose={() => setPanel(null)} title="更多模組">
          <Stagger className="grid grid-cols-3 gap-2">
            {allNav.map((item) => {
              const Icon = item.icon;
              const isActive = active === item.id;
              return (
                <StaggerItem key={item.id}>
                  <motion.button
                    type="button"
                    onClick={() => goTo(item.id)}
                    whileTap={{ scale: 0.96 }}
                    className={cn(
                      "flex w-full flex-col items-center gap-2 rounded-2xl border border-border/60 p-4 text-foreground/85 transition-colors",
                      isActive
                        ? "border-primary bg-primary/10 text-primary"
                        : "bg-card hover:bg-secondary",
                      item.locked && "border-border/40 bg-muted/30 text-muted-foreground opacity-70 hover:bg-muted/30",
                    )}
                  >
                    <Icon className="h-7 w-7" aria-hidden />
                    <span className="text-xs font-medium">{item.label}</span>
                    {item.locked && (
                      <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider">
                        Core
                      </span>
                    )}
                  </motion.button>
                </StaggerItem>
              );
            })}
          </Stagger>

          <a
            href="/"
            className="mt-5 block rounded-2xl border border-border/60 bg-card p-4 text-sm transition-colors hover:border-primary/40"
          >
            <div className="font-medium">升級至 Core</div>
            <p className="mt-1 text-xs text-muted-foreground">
              解鎖 FamMatch 不限次數配對、文件託管 5 GB
            </p>
            <span className="mt-3 inline-flex h-9 items-center justify-center rounded-full bg-primary px-4 text-xs font-medium text-primary-foreground">
              了解方案
            </span>
          </a>
        </BottomSheet>
      )}
      </AnimatePresence>

      {/* ─────────────────────  Menu (profile) bottom sheet  ───────────────────── */}
      <AnimatePresence>
      {menuOpen && (
        <BottomSheet onClose={() => setPanel(null)} title="目錄">
          <div className="flex items-center gap-3 rounded-2xl bg-secondary/40 p-3">
            <ProfileAvatar initials={me.avatarInitials} avatarUrl={me.avatarUrl} size="md" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{me.displayName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {me.email ?? "—"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => me.signOut().then(() => { window.location.href = "/login"; })}
              className="rounded-full px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              登出
            </button>
          </div>
          <Stagger className="mt-4 space-y-1" gap={0.04}>
            {[
              { icon: Bell, label: "通知", hint: notif.unreadCount > 0 ? `${notif.unreadCount} 則新訊息` : "已全部已讀", onClick: () => openPanel("notif") },
              { icon: Settings, label: "設定", onClick: () => openPanel("settings") },
              { icon: Search, label: "搜尋家人、對話、模組", onClick: () => openPanel("search") },
              { icon: TreePine, label: "我的 FamTree", onClick: () => goTo("famtree") },
              // Org-side entry — visible only to org_admin.
              // Post-04917 the 'org_member' role is no longer assigned;
              // org_admin is the only org-side role. The "機構後台"
              // link takes them to /org (the OrgShell).
              ...(me.role === "org_admin"
                ? [{ icon: Building2, label: "機構後台", hint: "管理本機構", onClick: () => { window.location.href = "/org"; } }]
                : []),
            ].map(({ icon: Icon, label, hint, onClick }) => (
              <StaggerItem key={label}>
                <motion.button
                  type="button"
                  onClick={onClick}
                  whileTap={{ scale: 0.98 }}
                  className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary active:bg-secondary/70"
                >
                  <Icon className="h-5 w-5 text-foreground/70" aria-hidden />
                  <span className="flex-1 font-medium">{label}</span>
                  {hint && (
                    <span className="text-xs text-muted-foreground">{hint}</span>
                  )}
                </motion.button>
              </StaggerItem>
            ))}
          </Stagger>

          <button
            type="button"
            onClick={() => me.signOut().then(() => { window.location.href = "/login"; })}
            className="mt-5 block w-full rounded-2xl border border-border/60 bg-card px-4 py-3 text-center text-sm font-medium text-foreground/85 transition-colors active:bg-secondary"
          >
            登出
          </button>

          <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-foreground">
            © {new Date().getFullYear()} FamChest 傳琛 · All rights reserved.
            <br />
            此為示範介面，所有資料皆為假資料
          </p>
        </BottomSheet>
      )}
      </AnimatePresence>

      {/* ─────────────────────  Settings panel  ─────────────────────
          Lightweight settings surface. The LITE build has only one
          per-role entry — "管理家庭成員" — that takes the family
          admin to the existing ModuleMembersView (which is the
          single place that handles the 6-member cap UI + invites).
          Non-admins see a "僅限家庭管理員" placeholder so the entry
          isn't silently hidden if a regular member opens this sheet. */}
      <AnimatePresence>
      {settingsOpen && (
        <BottomSheet onClose={() => setPanel(null)} title="設定">
          <div className="flex items-center gap-3 rounded-2xl bg-secondary/40 p-3">
            <ProfileAvatar initials={me.avatarInitials} avatarUrl={me.avatarUrl} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{me.displayName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {me.email ?? "—"}
              </div>
            </div>
          </div>

          <Stagger className="mt-4 space-y-1" gap={0.04}>
            {isFamilyAdmin && (
              <StaggerItem>
                <motion.button
                  type="button"
                  onClick={() => goTo("fammembers")}
                  whileTap={{ scale: 0.98 }}
                  className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary active:bg-secondary/70"
                >
                  <UserCog className="h-5 w-5 text-foreground/70" aria-hidden />
                  <span className="flex-1 font-medium">管理家庭成員</span>
                  <span className="text-xs text-muted-foreground">
                    新增、升級、移除
                  </span>
                </motion.button>
              </StaggerItem>
            )}

            {/* Org picker — every family is in exactly one org (PRD §6.2
                PRO). The family admin can switch which org serves the
                family; pending match_requests for this family are
                auto-declined by the change_family_org RPC. */}
            {isFamilyAdmin && (
              <StaggerItem>
                <motion.button
                  type="button"
                  onClick={() => openPanel("orgpicker")}
                  whileTap={{ scale: 0.98 }}
                  className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary active:bg-secondary/70"
                >
                  <Building2 className="h-5 w-5 text-foreground/70" aria-hidden />
                  <span className="flex-1 font-medium">揀選配對機構</span>
                  <span className="text-xs text-muted-foreground">
                    {currentOrg.data?.name ?? "FamChest 平台"}
                  </span>
                </motion.button>
              </StaggerItem>
            )}

            {!isFamilyAdmin && (
              <StaggerItem>
                <div className="flex items-center gap-3 rounded-2xl bg-secondary/30 px-3 py-2.5 text-sm text-muted-foreground">
                  <UserCog className="h-5 w-5" aria-hidden />
                  <span className="flex-1">管理家庭成員</span>
                  <span className="text-[11px]">僅限家庭管理員</span>
                </div>
              </StaggerItem>
            )}

            {/* Personal-account rows — same UI for both roles so the
                sheet doesn't visually change shape based on role.
                「上傳頭像」 opens the avatar-upload sheet; 「重設密碼」
                triggers Supabase's password-recovery email to the
                signed-in user's address (the existing /reset-password
                page then collects the new password when the user
                follows the link in the email). */}
            <StaggerItem>
              <motion.button
                type="button"
                onClick={() => openPanel("avatar")}
                whileTap={{ scale: 0.98 }}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary active:bg-secondary/70"
              >
                <Camera className="h-5 w-5 text-foreground/70" aria-hidden />
                <span className="flex-1 font-medium">上傳頭像</span>
                <span className="text-xs text-muted-foreground">
                  {me.avatarUrl ? "更換或移除" : "新增相片"}
                </span>
              </motion.button>
            </StaggerItem>
            <StaggerItem>
              <motion.button
                type="button"
                onClick={() => openPanel("password")}
                whileTap={{ scale: 0.98 }}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary active:bg-secondary/70"
              >
                <KeyRound className="h-5 w-5 text-foreground/70" aria-hidden />
                <span className="flex-1 font-medium">重設密碼</span>
                <span className="text-xs text-muted-foreground">
                  寄連結到電郵
                </span>
              </motion.button>
            </StaggerItem>
          </Stagger>

          <button
            type="button"
            onClick={() => me.signOut().then(() => { window.location.href = "/login"; })}
            className="mt-5 block w-full rounded-2xl border border-border/60 bg-card px-4 py-3 text-center text-sm font-medium text-foreground/85 transition-colors active:bg-secondary"
          >
            登出
          </button>

          <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-foreground">
            © {new Date().getFullYear()} FamChest 傳琛 · All rights reserved.
            <br />
            此為示範介面，所有資料皆為假資料
          </p>
        </BottomSheet>
      )}
      </AnimatePresence>

      {/* ─────────────────────  Avatar sheet  ─────────────────────
          Lets the user pick a local image, preview it, and upload
          to the public `avatars` bucket. Then the new URL is
          written to profiles.avatar_url in the same flow so a
          refreshProfile() after success updates the Settings
          preview and the top-bar trigger without a hard reload.

          The RLS policy `avatars self write` (migration 014) only
          allows INSERT, so each upload is to a fresh path; previous
          files stay in the bucket but are unreachable from the row
          — that's the orphan-cleanup model documented in
          useAvatarUpload.ts. The "移除頭像" path clears the row's
          avatar_url back to null and re-renders the initials chip. */}
      <AnimatePresence>
      {avatarOpen && (
        <AvatarSheet
          userId={me.user?.id ?? null}
          email={me.email}
          currentAvatarUrl={me.avatarUrl}
          initials={me.avatarInitials}
          state={avatarUpload.state}
          onPick={(file) => {
            if (!me.user?.id) return;
            void avatarUpload.upload(me.user.id, file).then((res) => {
              if (res) void me.refreshProfile();
            });
          }}
          onRemove={() => {
            if (!me.user?.id) return;
            void avatarUpload.remove(me.user.id).then((ok) => {
              if (ok) void me.refreshProfile();
            });
          }}
          onClose={closePanel}
        />
      )}
      </AnimatePresence>

      {/* ─────────────────────  Password-reset sheet  ─────────────────────
          Within the app, "reset password" means: send a
          password-recovery email to the signed-in user's address,
          with redirectTo landing on /reset-password (the existing
          page that opens after clicking the link in the email —
          see src/pages/ResetPassword.tsx). We deliberately do NOT
          inline the password-update UI here because that requires
          a Supabase session with the PASSWORD_RECOVERY event
          already fired, which only happens via the email link.
          Keeping the entry surface single-purpose ("send the link")
          avoids tempting users into a flow that would only half-
          work. */}
      <AnimatePresence>
      {passwordOpen && (
        <PasswordResetSheet
          email={me.email}
          onClose={closePanel}
        />
      )}
      </AnimatePresence>

      {/* ─────────────────────  Org picker sheet  ─────────────────────
          Lets the family admin switch the family's serving org. The
          change_family_org RPC auto-declines any pending match
          requests for the family. RLS `orgs_family_admin_browse`
          gates the approved-org list. */}
      <AnimatePresence>
      {orgPickerOpen && (
        <OrgPickerSheet
          currentOrgId={currentOrgId}
          currentOrgName={currentOrg.data?.name ?? null}
          familyId={cf.family?.id ?? null}
          onClose={() => setPanel(null)}
          onChanged={() => {
            currentOrg.refresh?.();
            cf.refresh?.();
            setPanel(null);
            window.location.reload();
          }}
        />
      )}
      </AnimatePresence>

      {/* ─────────────────────  Search panel  ───────────────────── */}
      <AnimatePresence>
      {searchOpen && (
        <BottomSheet onClose={() => setPanel(null)} title="搜尋">
          <SearchPanel onNavigate={goTo} onClose={() => setPanel(null)} />
        </BottomSheet>
      )}
      </AnimatePresence>

      {/* ─────────────────────  Notifications panel  ───────────────────── */}
      <AnimatePresence>
      {notifOpen && (
        <BottomSheet onClose={() => setPanel(null)} title="通知">
          <NotificationsPanel
            items={notif.items}
            unreadCount={notif.unreadCount}
            onMarkAllRead={notif.markAllRead}
            onMarkRead={notif.markRead}
            onNavigate={goTo}
            onClose={() => setPanel(null)}
          />
        </BottomSheet>
      )}
      </AnimatePresence>
    </div>
    <CommandPalette
      open={paletteOpen}
      onClose={() => setPaletteOpen(false)}
      items={paletteItems}
    />
    </ModuleSlotProvider>
  );
}

/* ─────────────────────  Today view (mobile-first cards)  ───────────────────── */
// Implementation lives in pages/modules/TodayView.tsx; re-exported here
// to keep the existing module-routing code working without changes.
export { TodayView } from "./modules/TodayView";

/* ─────────────────────  Module placeholder  ───────────────────── */

function ModulePlaceholder({ id }: { id: ModuleId }) {
  const item = allNav.find((n) => n.id === id);
  if (!item) return null;
  const Icon = item.icon;
  return (
    <div className="px-1 pt-2">
      <div className="rounded-2xl border border-dashed border-border/60 bg-secondary/20 p-10 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
          <Icon className="h-7 w-7" aria-hidden />
        </div>
        <h2 className="mt-4 font-serif text-xl font-semibold sm:text-2xl">
          {item.label}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {item.label} 模組正在 LITE 階段陸續推出。
          升級至 Core 即可搶先體驗完整功能。
        </p>
        <button
          type="button"
          onClick={() => history.back()}
          className="mt-5 inline-flex h-11 items-center rounded-full border border-border/60 bg-background px-5 text-sm font-medium hover:bg-secondary"
        >
          返回
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Settings-panel sub-sheets
// =====================================================================

/**
 * AvatarSheet — owned by AppShell's panel state, opened from the
 * Settings sheet. Wraps the file-picker + upload lifecycle so the
 * caller (AppShell) only has to forward `onPick` / `onRemove` callbacks
 * and read back the URL through refreshProfile().
 *
 * Local-only image preview uses object URL + revokes it on unmount and
 * on each pick-replacement. Doesn't upload to Storage until the user
 * confirms with 「儲存」, so they can change their mind without
 * leaving junk in the bucket.
 */
function AvatarSheet({
  userId,
  email,
  currentAvatarUrl,
  initials,
  state,
  onPick,
  onRemove,
  onClose,
}: {
  userId: string | null;
  email: string | null;
  currentAvatarUrl: string | null;
  initials: string;
  state: import("@/lib/useAvatarUpload").AvatarUploadState;
  onPick: (file: File) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  // Manage the object URL lifetime so we never leak references when
  // the user picks a new file or closes the sheet mid-selection.
  useEffect(() => {
    if (!pending) {
      setPendingUrl(null);
      return;
    }
    const url = URL.createObjectURL(pending);
    setPendingUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);

  // Reset pending file whenever the upload settles — so a successful
  // upload replaces the preview with the freshly-persisted URL,
  // and an error returns the sheet to its idle state without an
  // unreadable pending blob still being shown.
  useEffect(() => {
    if (state.phase === "done" || state.phase === "error" || state.phase === "idle") {
      setPending(null);
      if (typeof fileInputRef.current !== "undefined" && fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [state.phase]);

  const busy =
    state.phase === "compressing" ||
    state.phase === "uploading" ||
    state.phase === "saving";

  const phaseLabel = ((): string => {
    switch (state.phase) {
      case "compressing": return "調整大小中…";
      case "uploading":   return "上傳中…";
      case "saving":      return "儲存中…";
      default:            return "";
    }
  })();

  // What to show in the preview circle: freshly chosen file first,
  // then the persisted Storage URL, then the initials fallback. The
  // optimistic-pending file is the only one a user has interacted
  // with, so it's worth showing before the upload completes.
  const previewSrc = pendingUrl ?? currentAvatarUrl;

  return (
    <BottomSheet onClose={onClose} title="更換頭像">
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">
          揀選一張相片做你嘅頭像，支援 PNG / JPEG / WebP，上限 5 MiB。
          我哋會自動調整大小並壓縮。
        </p>

        <div className="flex flex-col items-center gap-4 rounded-2xl bg-secondary/30 p-6">
          <span
            aria-hidden
            className={cn(
              "grid h-28 w-28 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-rose-400 to-rose-600 text-3xl font-semibold text-white shadow-md",
              busy && "opacity-70",
            )}
          >
            {previewSrc ? (
              <img
                src={previewSrc}
                alt=""
                className="h-full w-full object-cover"
                key={previewSrc}
              />
            ) : (
              initials
            )}
          </span>

          {phaseLabel && (
            <p role="status" className="text-xs text-muted-foreground">
              {phaseLabel}
            </p>
          )}

          {!userId ? (
            <p role="alert" className="text-xs text-rose-700">
              未登入，無法更新頭像。
            </p>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-4 w-4" aria-hidden />
                {currentAvatarUrl ? "更換相片" : "選擇圖片"}
              </Button>
              {currentAvatarUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    if (typeof window !== "undefined" && !window.confirm("確定移除現有頭像？")) return;
                    onRemove();
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  移除現有頭像
                </Button>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/*"
            className="sr-only"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) setPending(f);
            }}
          />
        </div>

        {pending && !busy && (
          <div className="rounded-2xl border border-border/60 bg-card p-3">
            <p className="text-sm">
              已選擇「<span className="font-medium">{pending.name}</span>」（{(pending.size / 1024).toFixed(0)} KiB）。
              確定上傳？
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={() => onPick(pending)}
              >
                儲存頭像
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setPending(null)}
              >
                取消
              </Button>
            </div>
          </div>
        )}

        {state.phase === "error" && (
          <div
            role="alert"
            className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {state.message}
          </div>
        )}

        {state.phase === "done" && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            頭像已更新，正在應用到全 App。
          </div>
        )}

        {email && (
          <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
            上傳嘅相片屬於帳戶 <span className="font-medium text-foreground/80">{email}</span>，
            儲存於公開 avatars 容器。
          </p>
        )}
      </div>
    </BottomSheet>
  );
}

/**
 * PasswordResetSheet — sends Supabase's password-recovery email to
 * the signed-in user's address and shows a confirmation. The link
 * in the email opens /reset-password (the existing standalone page,
 * wired up by the Login-page's "忘記密碼？" link AND the entry
 * in the ForgotPassword page).
 */
function PasswordResetSheet({
  email,
  onClose,
}: {
  email: string | null;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [notice, setNotice] = useState<string | null>(null);

  const send = async () => {
    if (!email) {
      setNotice("呢個帳戶冇電郵地址可以寄出連結。");
      setPhase("error");
      return;
    }
    if (!isAuthConfigured()) {
      setNotice("尚未設定 Supabase 環境變數。請檢查 .env。");
      setPhase("error");
      return;
    }
    setPhase("sending");
    setNotice(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // The Supabase email-link callback must land on the same origin
      // as the running app, and the destination page needs to be the
      // standalone /reset-password (it runs its own session check
      // and PASSWORD_RECOVERY subscription, see pages/ResetPassword.tsx).
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      setPhase("error");
      setNotice("無法發送重設連結：" + error.message);
      return;
    }
    setPhase("sent");
  };

  return (
    <BottomSheet onClose={onClose} title="重設密碼">
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">
          輸入你註冊 FamChest 時嘅電郵，我哋會寄出重設密碼嘅連結。
          點擊連結後可以設定新密碼。
        </p>

        <div className="rounded-2xl bg-secondary/40 p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            帳戶電郵
          </div>
          <div className="mt-1 truncate text-sm font-medium">{email ?? "—"}</div>
        </div>

        {phase === "sent" ? (
          <div
            role="status"
            className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
          >
            <Mail className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div className="space-y-1">
              <p>重設連結已寄出。請檢查收件匣或垃圾郵件。</p>
              <p className="text-xs">
                連結會帶你去「設定新密碼」頁，揀好新密碼後可以用新密碼重新登入。
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {notice && (
              <div
                role="alert"
                className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {notice}
              </div>
            )}
            <Button
              type="button"
              className="h-11 w-full text-base"
              disabled={phase === "sending" || !email}
              onClick={() => void send()}
            >
              {phase === "sending" ? "發送中…" : "寄出重設連結"}
            </Button>
          </div>
        )}

        {phase === "sent" && (
          <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
            為咗安全起見，你可以等幾分鐘之後再喺其他裝置登入。
          </p>
        )}
      </div>
    </BottomSheet>
  );
}

export default AppShell;

